import {
  businessEntityKeys,
  deleteBusinessEntity,
  queryClient,
} from '@commenda/api';

import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingIndicator,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { OnboardingStepNotificationEntity } from '@commenda/types/src/company/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

const DeleteEntityModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess?: (corporation: OnboardingStepNotificationEntity) => Promise<void>;
}) => {
  const { setNotification } = useNotification();

  const legalName = businessEntity?.generalCorporationInfo?.legalName ?? '';
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirmation('');
  }, [isOpen]);

  if (!businessEntity) return null;

  const canDelete = confirmation === legalName && !isDeleting;

  const onDeleteEntity = async () => {
    if (!canDelete) return;
    setIsDeleting(true);

    try {
      const response = await deleteBusinessEntity(businessEntity.id);

      if (!response.ok) {
        setNotification({
          title: 'Failed to delete entity',
          message: 'Please try again or contact engineering if this persists.',
          valid: false,
          show: true,
        });
        return;
      }

      if (onSuccess) {
        await onSuccess({
          corporationType:
            businessEntity.generalCorporationInfo.businessEntityType,
          incorporationCountry:
            businessEntity.generalCorporationInfo.incorporationCountry,
          name: businessEntity.generalCorporationInfo.legalName,
        });
      }

      await queryClient.invalidateQueries({
        queryKey: [
          businessEntityKeys.FETCH_BUSINESS_ENTITIES_OF_COMPANY,
          +businessEntity.generalCorporationInfo.companyId,
        ],
      });

      setNotification({
        title: 'Entity deleted',
        message: `${legalName} has been permanently removed.`,
        valid: true,
        show: true,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setNotification({
        title: 'Failed to delete entity',
        message: 'An unexpected error occurred. Please try again.',
        valid: false,
        show: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="w-[520px] max-w-[95vw] flex flex-col">
        <DialogHeader>
          <DialogTitle>Delete entity</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-xl px-6 py-xl">
          <Alert
            variant="destructive"
            title="This cannot be undone"
            description={`Deleting ${legalName} will permanently remove every associated record — documents, compliance filings, directors, ownership, bank accounts, and service-request history. There is no recovery path once this completes.`}
          />

          <div className="flex flex-col gap-sm">
            <Label htmlFor="delete-entity-confirmation">
              To confirm, type the entity&apos;s legal name{'  '}
              <span className="txt-small-bold text-text-primary">
                {legalName}
              </span>
            </Label>
            <Input
              id="delete-entity-confirmation"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={legalName}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive-primary"
            size="sm"
            type="button"
            onClick={onDeleteEntity}
            disabled={!canDelete}
          >
            {isDeleting ? <LoadingIndicator size={16} /> : null}
            <span>Delete entity</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DeleteEntityModal };
